import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '../../components/ui/Button';
import { Button } from '../../components/ui/Button';
import { Select, Alert } from '../../components/ui/Modal';
import { gs } from '../../styles/globalStyles';
import { tradeService } from '../../services/trades';
import { KycDocument } from '../../types';
import { statusBadge } from '../../components/ui/Button';
import { formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

const KYC: React.FC = () => {
  const [kyc, setKyc] = useState<KycDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [docType, setDocType] = useState('passport');
  const [files, setFiles] = useState<{ front?: File; back?: File; selfie?: File }>({});

  useEffect(() => {
    tradeService.getKycStatus().then(res => setKyc(res.data.kyc)).catch(() => {});
  }, []);

  const handleFile = (field: 'front' | 'back' | 'selfie', file?: File) => {
    if (file) setFiles(prev => ({ ...prev, [field]: file }));
  };

  const submit = async () => {
    if (!files.front || !files.selfie) {
      toast.error('Front image and selfie are required');
      return;
    }

    const formData = new FormData();
    formData.append('document_type', docType);
    formData.append('front_image', files.front);
    if (files.back) formData.append('back_image', files.back);
    formData.append('selfie_image', files.selfie);

    setLoading(true);
    try {
      await tradeService.submitKyc(formData);
      toast.success('KYC submitted! We will review your documents shortly.');
      const res = await tradeService.getKycStatus();
      setKyc(res.data.kyc);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to submit KYC');
    } finally {
      setLoading(false);
    }
  };

  if (kyc?.status === 'approved') {
    return (
      <div className="p-6">
        <Card glow className="flex flex-col items-center py-10 text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4 shadow-glow">
            <Ico name="shield_check" className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-display text-xl font-bold text-gray-50">KYC Verified</h3>
          <p className="text-gray-400 text-sm mt-2">Your identity has been verified successfully.</p>
          <p className="text-xs text-gray-500 mt-4">Verified on {formatDate(kyc.reviewed_at || '')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Status card */}
      {kyc && (
        <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
          kyc.status === 'pending' ? 'bg-yellow-500/10 border-yellow-500/20' :
          kyc.status === 'rejected' ? 'bg-red-500/10 border-red-500/20' : ''
        }`}>
          <Ico name="shield" />
          <div>
            <p className="font-semibold text-gray-50 text-sm">KYC Status: {statusBadge(kyc.status)}</p>
            {kyc.admin_note && <p className="text-gray-400 text-xs mt-1">{kyc.admin_note}</p>}
          </div>
        </div>
      )}

      <Card>
        <div className="flex flex-col items-center gap-2 mb-6 pb-4 border-b border-gray-800/40">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Ico name="shield" className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-gray-50">Identity Verification</h3>
        </div>

        {kyc?.status === 'pending' ? (
          <Alert type="info" title="Under Review" message="Your KYC submission is being reviewed. This typically takes 1-2 business days." />
        ) : (
          <div className="flex flex-col gap-5">
            <Alert type="info" message="KYC verification increases your withdrawal limits and platform trust level. Please upload clear, legible documents." />

            <Select
              label="Document Type"
              options={[
                { value: 'passport', label: '🛂 Passport' },
                { value: 'id_card', label: '🪪 National ID Card' },
                { value: 'drivers_license', label: '🚗 Driver\'s License' },
              ]}
              value={docType}
              onChange={e => setDocType(e.target.value)}
            />

            <div className="grid grid-cols-1 gap-4">
              {[
                { field: 'front' as const, label: 'Front of Document', required: true },
                { field: 'back' as const, label: 'Back of Document (if applicable)', required: false },
                { field: 'selfie' as const, label: 'Selfie with Document', required: true },
              ].map(item => (
                <div key={item.field}>
                  <label className={gs.label}>
                    {item.label} {item.required && <span className="text-red-500">*</span>}
                  </label>
                  <label className="block">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={e => handleFile(item.field, e.target.files?.[0])}
                    />
                    <div className={`flex items-center justify-center gap-3 py-6 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                      files[item.field]
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-gray-700/60 bg-gray-800/30 hover:border-gray-600'
                    }`}>
                      {files[item.field] ? (
                        <>
                          <Ico name="file" className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-primary text-sm font-semibold">{files[item.field]!.name}</p>
                            <p className="text-gray-400 text-xs">{(files[item.field]!.size / 1024).toFixed(0)} KB</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Ico name="upload" className="w-5 h-5 text-gray-500" />
                          <div className="text-center">
                            <p className="text-gray-400 text-sm">Click to upload</p>
                            <p className="text-gray-600 text-xs">JPG, PNG, PDF up to 5MB</p>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>

            <Button
              fullWidth
              loading={loading}
              onClick={submit}
              icon={<Ico name="shield" />}
              disabled={!files.front || !files.selfie}
            >
              Submit for Verification
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default KYC;
